import { ajax_json_request } from "@/components/Settings/settings_utils";
import {
    get_auth_headers,
    get_user_friendly_error_message,
    users_url
} from "../settings_user_management_api";
import {
    get_session_policy_label,
    normalize_session_policy,
    session_policy_custom,
    session_policy_unlimited
} from "../settings_user_management_utils";

export default {
    name: "SettingsUserManagementTable",

    props: {
        users: {
            type: Array,
            default: () => []
        },
        property_options: {
            type: Array,
            default: () => []
        },
        is_loading: {
            type: Boolean,
            default: false
        },
        has_users_endpoint: {
            type: Boolean,
            default: false
        },
        current_user_id: {
            type: [String, Number],
            default: null
        },
        current_user_email: {
            type: String,
            default: ""
        }
    },

    emits: ["users-updated", "reload-users", "status-message", "error-message"],

    data() {
        return ({
            updating_status_user_id: null
        });
    },

    computed: {
        is_busy() {
            return this.updating_status_user_id !== null;
        }
    },

    methods: {
        open_edit_user(user) {
            sessionStorage.setItem("edit_user_profile", JSON.stringify(user || {}));
            sessionStorage.setItem("edit_user_property_options", JSON.stringify(this.property_options || []));
            this.$router.push({
                path: "/edit_profile",
                query: {
                    user_id: user.id,
                    return_to: "user_management"
                }
            });
        },

        async toggle_user_status(user) {
            if (this.is_self_deactivation(user)) {
                this.$emit("error-message", "You cannot deactivate your own account while you are signed in.");
                return;
            }

            const next_user = this.get_toggled_user(user);
            this.start_status_update(user);

            try {
                await this.persist_user_status(user, next_user);
                this.$emit("status-message", this.get_status_message(next_user));
            } catch (error) {
                this.$emit("error-message", get_user_friendly_error_message(error?.message, "We could not update this user's status. Please try again."));
            } finally {
                this.updating_status_user_id = null;
            }
        },

        is_self_deactivation(user) {
            return this.is_current_user(user) && user.is_active;
        },

        get_toggled_user(user) {
            return ({
                ...user,
                is_active: !user.is_active
            });
        },

        start_status_update(user) {
            this.updating_status_user_id = this.get_user_loading_key(user);
            this.$emit("error-message", "");
            this.$emit("status-message", "");
        },

        is_user_status_updating(user) {
            return this.updating_status_user_id === this.get_user_loading_key(user);
        },

        get_user_loading_key(user) {
            return String(user?.id || user?.email || "");
        },

        should_save_status_to_api(user, next_user) {
            return Boolean(user.id && users_url && next_user.is_active !== undefined);
        },

        async persist_user_status(user, next_user) {
            if (this.should_save_status_to_api(user, next_user)) {
                await this.save_user_status(user, next_user);
                await this.emit_reload_users();
                return;
            }

            this.emit_updated_users(next_user);
        },

        emit_updated_users(next_user) {
            this.$emit("users-updated", this.users.map((existing_user) => {
                return String(existing_user.id) === String(next_user.id) ? next_user : existing_user;
            }));
        },

        get_status_message(next_user) {
            return next_user.is_active ? "User activated." : "User deactivated.";
        },

        async save_user_status(user, next_user) {
            const payload = next_user.is_active
                ? { is_active: next_user.is_active }
                : null;

            await this.fetch_user_status_json(this.get_user_status_url(user, next_user), {
                method: "POST",
                headers: get_auth_headers(true),
                body: payload ? JSON.stringify(payload) : null
            });

            this.emit_updated_users(next_user);
        },

        get_user_status_url(user, next_user) {
            const base_url = this.get_user_update_base_url();
            return `${base_url}${user.id}/${next_user.is_active ? "activate/" : "deactivate/"}`;
        },

        get_user_update_base_url() {
            return users_url.endsWith("/") ? users_url : `${users_url}/`;
        },

        fetch_user_status_json(request_url, options = {}) {
            if (!request_url) {
                throw new Error("User status changes are not fully set up yet.");
            }

            return ajax_json_request(request_url, options);
        },

        emit_reload_users() {
            return new Promise((resolve) => {
                this.$emit("reload-users", resolve);
            });
        },

        get_full_name(user) {
            return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || "User";
        },

        get_initials(user) {
            const first = user.first_name?.charAt(0) || "";
            const last = user.last_name?.charAt(0) || "";
            const fallback = user.email?.charAt(0) || "U";
            return (first + last || fallback).toUpperCase();
        },

        get_property_summary(user) {
            if (!user.assigned_properties?.length) {
                return "No properties assigned";
            }

            if (user.assigned_properties.length <= 2) {
                return user.assigned_properties.map((property) => property.name).join(", ");
            }

            return `${user.assigned_properties[0].name}, ${user.assigned_properties[1].name} +${user.assigned_properties.length - 2}`;
        },

        get_session_label(user) {
            return get_session_policy_label(user?.session_policy, user?.session_timeout_minutes);
        },

        get_session_badge_class(user) {
            const policy = normalize_session_policy(user?.session_policy);

            if (policy === session_policy_unlimited) {
                return "text-bg-warning bg-opacity-25 border border-warning border-opacity-25";
            }

            if (policy === session_policy_custom) {
                return "text-bg-primary bg-opacity-25 border border-primary border-opacity-25";
            }

            return "text-bg-secondary bg-opacity-25 border border-secondary-subtle";
        },

        get_session_title(user) {
            const policy = normalize_session_policy(user?.session_policy);

            if (policy === session_policy_unlimited) {
                return "This user is never signed out for inactivity";
            }

            if (policy === session_policy_custom) {
                return `Signed out after ${this.get_session_label(user)} of inactivity`;
            }

            return "Uses the system default inactivity timeout";
        },

        get_last_login_label(user) {
            if (!user?.last_login) {
                return "Never";
            }

            const last_login = new Date(user.last_login);
            if (Number.isNaN(last_login.getTime())) {
                return String(user.last_login);
            }

            return last_login.toLocaleString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            });
        },

        is_current_user(user) {
            const user_id = user?.id || null;
            const user_email = String(user?.email || "").toLowerCase();

            if (this.current_user_id && String(user_id) === String(this.current_user_id)) {
                return true;
            }

            return this.current_user_email ? user_email === this.current_user_email : false;
        },

        get_status_action_title(user) {
            if (this.is_current_user(user) && user.is_active) {
                return "You cannot deactivate your own account";
            }

            return !user.is_active && !this.has_users_endpoint
                ? "Activate endpoint is not available"
                : "";
        }
    }
};
